/**
 * Local metre frame for the virtual course.
 *
 * An equirectangular tangent plane around the course centroid: `x` grows
 * east and `y` grows north, both in metres. Over a 100 km course the scale
 * error stays under 0.3 %, far below what a stylised model needs, and it
 * keeps the ribbon, terrain and markers in one frame with no renderer
 * dependency. Scene code maps this frame to three.js as
 * `(x, up, -y)`; see `toScene` in the scene components.
 */

export interface LngLat {
  latitude: number
  longitude: number
}

export interface LocalPoint {
  x: number
  y: number
}

export interface LngLatBounds {
  north: number
  south: number
  east: number
  west: number
}

export interface LocalBounds {
  minX: number
  maxX: number
  minY: number
  maxY: number
}

export interface Projection {
  readonly origin: LngLat
  readonly metresPerDegreeLng: number
  readonly metresPerDegreeLat: number
  toLocal(point: LngLat): LocalPoint
  toLngLat(point: LocalPoint): LngLat
}

export const EARTH_RADIUS_M = 6_371_008.8
const DEG = Math.PI / 180

export function createProjection(origin: LngLat): Projection {
  const metresPerDegreeLat = EARTH_RADIUS_M * DEG
  const metresPerDegreeLng = Math.cos(origin.latitude * DEG) * metresPerDegreeLat
  return {
    origin,
    metresPerDegreeLng,
    metresPerDegreeLat,
    toLocal: (point) => ({
      x: (point.longitude - origin.longitude) * metresPerDegreeLng,
      y: (point.latitude - origin.latitude) * metresPerDegreeLat,
    }),
    toLngLat: (point) => ({
      latitude: origin.latitude + point.y / metresPerDegreeLat,
      longitude: origin.longitude + point.x / metresPerDegreeLng,
    }),
  }
}

export function centroidOf(points: LngLat[]): LngLat {
  if (!points.length) throw new Error('centroidOf: no points')
  let latitude = 0
  let longitude = 0
  for (const point of points) {
    latitude += point.latitude
    longitude += point.longitude
  }
  return { latitude: latitude / points.length, longitude: longitude / points.length }
}

export function boundsOf(points: LngLat[]): LngLatBounds {
  if (!points.length) throw new Error('boundsOf: no points')
  const bounds = { north: -Infinity, south: Infinity, east: -Infinity, west: Infinity }
  for (const point of points) {
    bounds.north = Math.max(bounds.north, point.latitude)
    bounds.south = Math.min(bounds.south, point.latitude)
    bounds.east = Math.max(bounds.east, point.longitude)
    bounds.west = Math.min(bounds.west, point.longitude)
  }
  return bounds
}

/** Grow bounds by a distance in metres on every side. */
export function padBounds(bounds: LngLatBounds, metres: number): LngLatBounds {
  const latitude = (bounds.north + bounds.south) / 2
  const dLat = metres / (EARTH_RADIUS_M * DEG)
  const dLng = metres / (Math.cos(latitude * DEG) * EARTH_RADIUS_M * DEG)
  return {
    north: bounds.north + dLat,
    south: bounds.south - dLat,
    east: bounds.east + dLng,
    west: bounds.west - dLng,
  }
}

export function localBoundsOf(points: LocalPoint[]): LocalBounds {
  const bounds = { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity }
  for (const point of points) {
    bounds.minX = Math.min(bounds.minX, point.x)
    bounds.maxX = Math.max(bounds.maxX, point.x)
    bounds.minY = Math.min(bounds.minY, point.y)
    bounds.maxY = Math.max(bounds.maxY, point.y)
  }
  return bounds
}

export function padLocalBounds(bounds: LocalBounds, metres: number): LocalBounds {
  return {
    minX: bounds.minX - metres,
    maxX: bounds.maxX + metres,
    minY: bounds.minY - metres,
    maxY: bounds.maxY + metres,
  }
}

export function localBoundsToLngLat(bounds: LocalBounds, projection: Projection): LngLatBounds {
  const southWest = projection.toLngLat({ x: bounds.minX, y: bounds.minY })
  const northEast = projection.toLngLat({ x: bounds.maxX, y: bounds.maxY })
  return { north: northEast.latitude, south: southWest.latitude, east: northEast.longitude, west: southWest.longitude }
}
